import React, { useEffect, useState, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import { useAuth } from '../services/authContext';
import { useToast } from '../components/Toast';
import SetupGuard from '../components/SetupGuard';

const SEMESTERS = ['Semester 1', 'Semester 2', 'Full Academic Year'];

const Materials = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin';
  const [materials, setMaterials]       = useState([]);
  const [classes, setClasses]           = useState([]);
  const [modules, setModules]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const [search, setSearch]             = useState('');
  const [filterClass, setFilterClass]   = useState('');
  const [isModalOpen, setModal]         = useState(false);
  const [downloads, setDownloads]       = useState(null);
  const [loadError, setLoadError]       = useState('');
  const [form, setForm]                 = useState({ classId: '', moduleId: '', title: '', description: '', topic: '', semester: '', file: null });

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      setLoadError('');

      // Load all data in parallel
      const [mats, cls, mods] = await Promise.all([
        apiService.getAll('materials').catch(err => {
          console.error('Failed to load materials:', err);
          return [];
        }),
        apiService.getAll('classes').catch(err => {
          console.error('Failed to load classes:', err);
          return [];
        }),
        apiService.getAll('modules').catch(err => {
          console.error('Failed to load modules:', err);
          return [];
        }),
      ]);

      // Check if critical data loaded
      if (!Array.isArray(mats)) {
        console.warn('Materials response invalid');
        setMaterials([]);
      } else {
        setMaterials(mats);
      }

      if (!Array.isArray(cls)) {
        console.warn('Classes response invalid');
        setClasses([]);
      } else {
        setClasses(cls);
      }

      if (!Array.isArray(mods)) {
        console.warn('Modules response invalid');
        setModules([]);
      } else {
        setModules(mods);
      }

      // Check if we have critical prerequisites
      if (cls.length === 0 && mods.length === 0) {
        setLoadError('Prerequisites not set up yet.');
      }
    } catch (err) {
      console.error('Error loading materials data:', err);
      setLoadError('Failed to load materials. Please refresh the page.');
      setMaterials([]);
      setClasses([]);
      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() =>
    materials.filter(m => {
      const matchSearch = [m.title, m.className, m.moduleName || m.subjectName, m.topic].join(' ').toLowerCase().includes(search.toLowerCase());
      const matchClass  = filterClass ? String(m.classId) === filterClass : true;
      return matchSearch && matchClass;
    }), [materials, search, filterClass]);

  const handleChange = e => {
    const { name, value, files } = e.target;
    setForm(p => ({ ...p, [name]: files ? files[0] : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validation
    if (!form.classId) {
      setError('Please select a class.');
      toast('Please select a class.', 'error');
      return;
    }

    if (!form.file) {
      setError('Please select a file to upload.');
      toast('Please select a file to upload.', 'error');
      return;
    }

    if (!form.title) {
      setError('Please enter a title for the material.');
      toast('Please enter a title for the material.', 'error');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const fd = new FormData();
      fd.append('file',        form.file);
      fd.append('classId',     form.classId);
      fd.append('moduleId',    form.moduleId);
      fd.append('title',       form.title);
      fd.append('description', form.description);
      fd.append('topic',       form.topic);
      fd.append('semester',    form.semester);

      await apiService.raw.post('materials', fd);

      setSuccess('Material uploaded successfully!');
      toast('Material uploaded successfully.', 'success');
      setModal(false);
      setForm({ classId: '', moduleId: '', title: '', description: '', topic: '', semester: '', file: null });
      await load();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Upload failed. Please try again.';

      // Handle specific error messages with suggestions
      let displayMsg = errorMsg;
      if (errorMsg.includes('not assigned')) {
        displayMsg = errorMsg + ' Contact your administrator to be assigned to this class.';
      } else if (errorMsg.includes('teacher account')) {
        displayMsg = 'Your account needs to be set up as a teacher first. Contact an administrator.';
      }

      setError(displayMsg);
      toast(displayMsg, 'error');
      console.error('Upload error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Are you sure you want to delete this material? This cannot be undone.')) return;
    try {
      await apiService.delete('materials', id);
      await load();
      toast('Material deleted.', 'success');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to delete.';
      setError(errorMsg);
      toast(errorMsg, 'error');
    }
  };

  const viewDownloads = async id => {
    try {
      const data = await apiService.get(`materials/${id}/downloads`);
      setDownloads({ id, list: Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []) });
    } catch (err) {
      console.error('Failed to load downloads:', err);
      setDownloads({ id, list: [] });
    }
  };

  const formatSize = bytes => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const tableContent = (
    <div className="table-responsive">
      <table className="data-table">
        <thead>
          <tr><th>Title</th><th>Class</th><th>Module</th><th>Topic</th><th>Size</th><th>Downloads</th><th>Uploaded</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={8} className="no-data-message">No materials found.</td></tr>
          ) : filtered.map(m => (
            <tr key={m.id}>
              <td><strong>{m.title}</strong>{m.semester && <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>{m.semester}</div>}</td>
              <td>{m.className}</td>
              <td>{m.moduleName || m.subjectName || '—'}</td>
              <td>{m.topic || '—'}</td>
              <td>{formatSize(m.fileSize)}</td>
              <td>
                <button className="btn-icon" onClick={() => viewDownloads(m.id)} style={{ fontSize: '0.85rem' }}>
                  {m.downloadCount} views
                </button>
              </td>
              <td>{new Date(m.createdAt).toLocaleDateString()}</td>
              <td>
                <div className="action-buttons">
                  <button className="btn-icon" onClick={async () => {
                    try {
                      const res = await apiService.raw.get(`materials/${m.id}/download`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      a.setAttribute('download', m.originalName || m.fileName || 'material');
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast('File downloaded.', 'success');
                    } catch (err) {
                      console.error('Download error:', err);
                      toast('Download failed. Please try again.', 'error');
                    }
                  }}>Download</button>
                  <button className="btn-icon" style={{ color: '#c62828' }} onClick={() => handleDelete(m.id)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <LoadingSpinner message="Loading materials..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Learning Materials</h1>
        <button className="btn btn-primary" onClick={() => setModal(true)}>+ Upload Material</button>
      </div>

      {error   && <div className="error-message">{error}</div>}
      {success && <div style={{ background: '#d4edda', color: '#155724', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem' }}>{success}</div>}
      {loadError && <div className="error-message">{loadError}</div>}

      {isAdmin ? (
        <SetupGuard checks={[
          {
            ok: classes.length > 0,
            icon: '🏫',
            label: 'No Classes exist yet',
            description: 'Create a class or batch before uploading materials.',
            action: { label: 'Create Class', to: '/programmes' },
          },
          {
            ok: modules.length > 0,
            icon: '📚',
            label: 'No Modules exist yet',
            description: 'Create modules so you can tag materials to a specific module.',
            action: { label: 'Create Module', to: '/modules' },
          },
        ]}>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <input style={{ flex: 1, minWidth: 200 }} type="text" placeholder="Search materials..." value={search} onChange={e => setSearch(e.target.value)} />
            <select value={filterClass} onChange={e => setFilterClass(e.target.value)} style={{ minWidth: 160 }}>
              <option value="">All Classes</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          {tableContent}
        </SetupGuard>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <input style={{ flex: 1, minWidth: 200 }} type="text" placeholder="Search materials..." value={search} onChange={e => setSearch(e.target.value)} />
            <select value={filterClass} onChange={e => setFilterClass(e.target.value)} style={{ minWidth: 160 }}>
              <option value="">All My Classes</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          {tableContent}
        </>
      )}

      {/* Upload modal */}
      <Modal isOpen={isModalOpen} onClose={() => setModal(false)} title="Upload Learning Material">
        <form onSubmit={handleSubmit} className="form">
          <div className="form-row">
            <div className="form-group">
              <label>Class *</label>
              <select name="classId" value={form.classId} onChange={handleChange} required>
                <option value="">Select Class</option>
                {classes.length === 0 ? (
                  <option disabled>No classes available. Create one first.</option>
                ) : (
                  classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)
                )}
              </select>
            </div>
            <div className="form-group">
              <label>Module (Optional)</label>
              <select name="moduleId" value={form.moduleId} onChange={handleChange}>
                <option value="">Select Module</option>
                {modules.length === 0 ? (
                  <option disabled>No modules available. Create one first.</option>
                ) : (
                  modules.map(m => <option key={m._id} value={m._id}>{m.code} - {m.name}</option>)
                )}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g. Chapter 5 Lecture Notes"
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Topic (Optional)</label>
              <input
                name="topic"
                value={form.topic}
                onChange={handleChange}
                placeholder="e.g. Chapter 3: Introduction to Polymorphism"
              />
            </div>
            <div className="form-group">
              <label>Semester (Optional)</label>
              <select name="semester" value={form.semester} onChange={handleChange}>
                <option value="">Select</option>
                {SEMESTERS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Description (Optional)</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder="Add details about this material to help students understand what it covers."
            />
          </div>
          <div className="form-group">
            <label>File * (PDF, Word, PPT, Excel, ZIP, Images, Video — max 50MB)</label>
            <input
              type="file"
              name="file"
              onChange={handleChange}
              required
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.txt"
              title="Select a document, image, or video file"
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Downloads modal */}
      <Modal isOpen={!!downloads} onClose={() => setDownloads(null)} title="Download History">
        {downloads && (
          downloads.list.length === 0 ? (
            <div className="no-data-message">No downloads yet.</div>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead><tr><th>Student</th><th>Email</th><th>Downloaded At</th></tr></thead>
                <tbody>
                  {downloads.list.map((d, i) => (
                    <tr key={i}>
                      <td>{d.first_name} {d.last_name}</td>
                      <td>{d.email}</td>
                      <td>{new Date(d.downloaded_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </Modal>
    </div>
  );
};

export default Materials;
